import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Check, Info } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SwaggerUI from 'swagger-ui-react'
import 'swagger-ui-react/swagger-ui.css'
import swaggerSpec from '../../../../../public/api-spec/v1/swagger.json'
import BannerImage from '../../components/BannerImage'
import CopyableApiKeyValue from '../../components/CopyableApiKeyValue'
import { useTranslation } from 'react-i18next'
import { LandingPage } from '../../components'
import LoginButtons from '../../components/LoginButtons'
import Select from '../../components/Select'
import {
  usePageInfo,
  useTeamDetailsById,
  useUserTeamAccess,
  useUserTeamPreferences,
} from '../../config/queries'
import { useCustomDomain } from '../../hooks/useCustomDomain'
import styles from './ApiOverview.module.scss'

const selectedTeamStorageKey = 'apiDocumentationSelectedTeamId'

interface SwaggerClient {
  preauthorizeApiKey: (scheme: string, value: string) => void
}

const TeamOption = ({
  team,
  selected = false,
}: {
  team: wowaudit.TeamReference
  selected?: boolean
}) => (
  <div className='flex w-full items-center justify-between gap-4'>
    <div className='flex min-w-0 items-center gap-3'>
      <BannerImage
        faction={team.ownerReference.faction}
        fileName={team.ownerReference.bannerFilename}
        size='small'
      />
      <div className='min-w-0'>
        <div className='truncate font-medium'>{team.ownerReference.name}</div>
        <div className='truncate text-xs text-muted-foreground'>
          {team.name}
        </div>
      </div>
    </div>
    <Check
      size={16}
      className={`min-w-4 ${selected ? 'opacity-100' : 'opacity-0'}`}
    />
  </div>
)

const TeamSelectLabel = ({ team }: { team: wowaudit.TeamReference }) => (
  <span className='flex min-w-0 items-center gap-2 text-left'>
    <BannerImage
      faction={team.ownerReference.faction}
      fileName={team.ownerReference.bannerFilename}
      size='small'
    />
    <span className='min-w-0'>
      <span className='block truncate font-medium'>
        {team.ownerReference.name}
      </span>
      <span className='block truncate text-xs text-muted-foreground'>
        {team.name}
      </span>
    </span>
  </span>
)

const ApiOverview = () => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const { customDomain } = useCustomDomain()
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(
    () => localStorage.getItem(selectedTeamStorageKey) ?? undefined,
  )

  const { data: teamPreferences, isPending: teamPreferencesPending } =
    useUserTeamPreferences(!!user)

  const teams = useMemo(() => {
    const guildTeams = (teamPreferences?.guilds ?? []).flatMap(
      (guild) => guild.teamReferences,
    )
    const teamsById = new Map<number, wowaudit.TeamReference>()
    for (const team of [...(user?.teamReferences ?? []), ...guildTeams]) {
      teamsById.set(team.id, team)
    }
    return Array.from(teamsById.values())
  }, [user?.teamReferences, teamPreferences?.guilds])

  const teamIds = useMemo(() => teams.map((team) => team.id), [teams])
  const { data: teamAccess, isPending: teamAccessPending } =
    useUserTeamAccess(teamIds)
  const loadingTeams =
    !!user &&
    (teamPreferencesPending || (teamIds.length > 0 && teamAccessPending))

  const adminTeams = useMemo(
    () =>
      teams
        .filter((team) => teamAccess?.[team.id.toString()])
        .sort((a, b) => {
          const ownerSort = a.ownerReference.name.localeCompare(
            b.ownerReference.name,
          )
          return ownerSort === 0 ? a.orderInGuild - b.orderInGuild : ownerSort
        }),
    [teamAccess, teams],
  )
  const customDomainTeamId = customDomain?.team?.id?.toString()
  const activeTeamId = customDomainTeamId ?? selectedTeamId
  const hasCustomDomainTeamAccess = customDomainTeamId
    ? !!teamAccess?.[customDomainTeamId]
    : undefined
  const hasNoAdminAccess = customDomainTeamId
    ? !hasCustomDomainTeamAccess
    : adminTeams.length === 0
  const selectedTeam = adminTeams.find(
    (team) => team.id.toString() === activeTeamId,
  )
  const { data: selectedTeamDetails } = useTeamDetailsById(selectedTeam?.id)
  const selectedPrivateKey = selectedTeamDetails?.privateKey
  const swaggerClient = useRef<SwaggerClient>()

  const handleTeamSelect = (teamId: string | undefined) => {
    setSelectedTeamId(teamId)

    if (teamId) {
      localStorage.setItem(selectedTeamStorageKey, teamId)
    }
  }

  const teamOptions = adminTeams.map((team) => ({
    value: team.id.toString(),
    label: <TeamSelectLabel team={team} />,
    sortLabel: `${team.ownerReference.name} ${team.name}`,
    customOptionFn: (selected: boolean) => (
      <TeamOption team={team} selected={selected} />
    ),
  }))

  const authorizeSwagger = useCallback(
    (client?: SwaggerClient) => {
      if (!client) return

      if (selectedPrivateKey) {
        client.preauthorizeApiKey('Bearer', selectedPrivateKey)
      }
    },
    [selectedPrivateKey],
  )

  useEffect(() => {
    authorizeSwagger(swaggerClient.current)
  }, [authorizeSwagger])

  const handleSwaggerComplete = (client: SwaggerClient) => {
    swaggerClient.current = client
    authorizeSwagger(client)
  }

  return (
    <LandingPage
      title={t('homepage.api-title')}
      description={t('homepage.api-description')}
      loading={loadingTeams}
    >
      <div className='space-y-6'>
        <p>
          You can interact with your team&apos;s data through API endpoints.
          With your team&apos;s API key your entire environment is approachable,
          so keep it safe. To authenticate, include the key as an Authorization
          header prefixed with <em>Bearer </em> or as an <em>api_key</em> query
          parameter.
        </p>

        {!user ? (
          <Alert>
            <AlertTitle>Not logged in</AlertTitle>
            <AlertDescription>
              <p className='mb-4'>
                Please log in to use the API documentation.
              </p>
              <LoginButtons withLabel={false} />
            </AlertDescription>
          </Alert>
        ) : hasNoAdminAccess ? (
          <Alert>
            <Info size={16} />
            <AlertTitle>No access</AlertTitle>
            <AlertDescription>
              You need to have admin access to{' '}
              {customDomainTeamId ? 'this team' : 'a team'} in order to use the
              API.
            </AlertDescription>
          </Alert>
        ) : selectedTeam ? (
          <div>
            <div className='flex justify-between items-end'>
              <div className='text-base font-medium'>API key</div>

              {customDomainTeamId ? null : (
                <Select
                  value={selectedTeam.id.toString()}
                  setValue={handleTeamSelect}
                  options={teamOptions}
                  placeholder='Select a team'
                  className='w-fit min-w-62 h-12 pl-2'
                />
              )}
            </div>
            <CopyableApiKeyValue
              value={selectedPrivateKey}
              tooltipContent='Click to copy'
              cardClassName='w-full min-h-10 truncate py-2 px-3 flex items-center justify-center'
              emptyNotice={
                <span className='opacity-50'>
                  No API key has been configured for this team
                </span>
              }
            />
          </div>
        ) : customDomainTeamId ? null : (
          <div className='flex justify-between items-center max-sm:flex-col gap-2'>
            <p className='text-sm text-muted-foreground'>
              Select a team to use its API key
            </p>
            <Select
              value={selectedTeamId}
              setValue={handleTeamSelect}
              options={teamOptions}
              placeholder='Select a team'
              className='w-fit min-w-62 h-12 pl-2'
            />
          </div>
        )}

        <div
          className={`${styles.swaggerContainer} ${
            selectedPrivateKey ? '' : styles.inactive
          }`}
        >
          <SwaggerUI spec={swaggerSpec} onComplete={handleSwaggerComplete} />
        </div>
      </div>
    </LandingPage>
  )
}

export default ApiOverview
